// The above eslint-disable is temporary while we migrate queries.js from JS to TS
import { Accordion, AccordionContent, AccordionItem, AccordionItemNumber, AccordionTrigger, Button, Dialog, DialogContent, DialogHeader, DialogTitle, ErrorContainer, IconCreate, IconTick, ScrollArea, Text } from "@sentricmusic/ui";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
// import { toast } from "sonner";
import { toast } from "sonner";
import { z } from "zod";

import { useCreateRecording, useUpdateSuggestion } from "@/api/mutations";
import { useGetArtistMeta, useGetSuggestions } from "@/api/queries";
import { apiCall } from "@/api/shared";
import useGetColours from "@/helpers/getColours";
import { sanitizeInput, sanitizeRecordLabel } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { CreateSongData } from "../../hooks/types";
import type { SelectedTrack, SourceType } from "../store/multiSongStore";
import type { SpotifyAlbumInfoResponse } from "../types/spotify";
import type { Writer } from "../types/writer";
import type { SongShareData } from "./DetailsAndSharesStep";

import { useSongOperations } from "../../hooks/useSongOperations";
import useMultiSongStore from "../store/multiSongStore";
import { useWizardStore } from "../store/wizardStore";
import useWriterStore from "../store/writerStore";

// Zod schema for song form validation (imported from previous step) - function to create dynamic validation based on context
function createSongFormSchema(isTunecore: boolean, availableWriters: Writer[]) {
  return z.object({
    songTitle: z
      .string()
      .min(1, "Song title is required")
      // .max(5, "Song title must be less than 5 characters"),
      .max(200, "Song title must be less than 200 characters"),
    durationMins: z
      .number()
      .min(0, "Duration minutes cannot be negative")
      .max(60, "Duration minutes must be less than 60"),
    durationSecs: z
      .number()
      .min(0, "Duration seconds cannot be negative")
      .max(59, "Duration seconds must be less than 60"),
    alternativeTitles: z.array(z.string()),
    performingArtists: z
      .array(z.string().min(1, "Performing artist name is required").max(100, "Artist name must be less than 100 characters"))
      .min(1, "At least one performing artist is required")
      .refine((values) => {
        return values.every((value) => {
          const sanitized = sanitizeInput(value);
          const safeSanitized = sanitized || "";
          return safeSanitized === value;
        });
      }, "One or more performing artist names contain invalid characters"),
    writers: z
      .array(z.object({
        writercode: z.string(),
        writername: z.string(),
        writershare: z.number().min(0).max(100),
        writerdesignationcode: z.number().min(1).max(3),
        righttocollect: z.boolean(),
      }))
      .min(1, "At least one writer is required")
      .refine((writers) => {
        const totalShares = writers.reduce((sum, writer) => sum + writer.writershare, 0);
        return Math.abs(totalShares - 100) < 0.01;
      }, "Total writer shares must equal 100%")
      .refine((writers) => {
        return writers.some(writer => writer.righttocollect === true);
      }, "At least one writer must have 'Right To Collect' enabled")
      .refine((writers) => {
        // Tunecore-specific validation: Writers with Right To Collect must have DOB and IPI
        if (!isTunecore)
          return true;

        const writersWithRTC = writers.filter(writer => writer.righttocollect === true);

        for (const writer of writersWithRTC) {
          const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
          if (!fullWriter)
            continue;

          const hasDateOfBirth = Boolean(fullWriter.dateofbirth != null && fullWriter.dateofbirth.trim() !== "");
          const hasCAEIPI = Boolean(fullWriter.caeipinumber != null && fullWriter.caeipinumber.trim() !== "");

          if (!hasDateOfBirth || !hasCAEIPI) {
            return false;
          }
        }

        return true;
      }, "For Tunecore submissions, writers with 'Right To Collect' must have both Date of Birth and IPI/CAE number"),
  });
}

// Validation progress tracking
type ValidationProgress = {
  isValidating: boolean;
  isUploading: boolean;
  isSuccess: boolean;
  currentSongIndex: number;
  totalSongs: number;
  uploadedSongs: number;
  validationErrors: Record<string, string[]>;
  hasValidationErrors: boolean;
};

export type SummaryAndSubmitStepProps = {
  source: SourceType;
  onSubmit?: (songShares: Record<string, SongShareData>) => Promise<void>;
  onValidationComplete?: (isValid: boolean, submitFn?: () => Promise<void>) => void;
};

// Writer designation codes mapping
const DESIGNATION_OPTIONS = [
  { value: 1, label: "Author" },
  { value: 2, label: "Composer" },
  { value: 3, label: "Composer/Author" },
];

export default function SummaryAndSubmitStep({ source, onSubmit, onValidationComplete }: SummaryAndSubmitStepProps) {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const { highlightColour2 } = useGetColours();

  const { tracks: selectedTracks, sourceType, sourceData, songShares, setCurrentSongIndex } = useMultiSongStore();
  // For album sources, sourceData contains the album info including label
  const albumData = sourceType === "album" ? sourceData as SpotifyAlbumInfoResponse | null : null;
  const { previousStep } = useWizardStore();
  const { availableWriters } = useWriterStore();

  // Fetch artist meta to get Spotify artist ID (likely cached from SongSuggestions page)
  const artistMeta = useGetArtistMeta({ aac: selectedAccount });
  const spotifyArtistId = React.useMemo(() => {
    try {
      const spotifyObj = artistMeta?.data?.spotify_obj;
      return (spotifyObj != null && spotifyObj !== "") ? (JSON.parse(spotifyObj) as { id?: string })?.id ?? null : null;
    }
    catch {
      return null;
    }
  }, [artistMeta?.data?.spotify_obj]);

  // Fetch song suggestions (likely cached from SongSuggestions page).
  // Used to determine which tracks have suggestion records before updating their status.
  const suggestionsQuery = useGetSuggestions({ spotifyid: spotifyArtistId ?? "", accountId: selectedAccount });
  const suggestedSpotifyIds = React.useMemo(() => {
    const suggestions = suggestionsQuery?.data as Array<{ spotify_song_id?: string }> | null | undefined;
    if (!Array.isArray(suggestions))
      return new Set<string>();
    return new Set(
      suggestions
        .map(s => s.spotify_song_id)
        .filter((id): id is string => typeof id === "string" && id.trim() !== ""),
    );
  }, [suggestionsQuery?.data]);

  // Handler to edit a specific song
  const handleEditSong = (songId: string) => {
    // Find the index of the song in the songDataArray
    const songDataArray = Object.values(songShares);
    const songIndex = songDataArray.findIndex(song => song.songId === songId);

    if (songIndex !== -1) {
      // Set the current song index in the store
      setCurrentSongIndex(songIndex);
      // Navigate back to the previous step (DetailsAndSharesStep)
      previousStep();
    }
  };

  // Helper function to check if song title has invalid characters
  const getTitleValidationInfo = (title: string) => {
    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      hasInvalidChars,
      suggestedTitle: safeSanitized || (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate alternative titles
  const validateAlternativeTitle = (title: string) => {
    if (title.length === 0)
      return { isValid: true, suggestedTitle: "" };

    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      isValid: !hasInvalidChars,
      suggestedTitle: safeSanitized.length > 0 ? safeSanitized : (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate performing artist names
  const validatePerformingArtist = (artistName: string) => {
    if (artistName.length === 0)
      return { isValid: true, suggestedName: "" };

    const sanitized = sanitizeInput(artistName);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== artistName;

    return {
      isValid: !hasInvalidChars,
      suggestedName: safeSanitized.length > 0 ? safeSanitized : (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // State to track error counts for each unique error message
  const [errorCounts, setErrorCounts] = useState<Record<string, number>>({});

  const updateSuggestionMutation = useUpdateSuggestion();
  const createRecordingMutation = useCreateRecording();

  const { createSongs } = useSongOperations({
    selectedAccount,
    onValidationError: (messages) => {
      console.error("Song validation error:", messages);

      // Handle empty messages array
      if (messages == null || (Array.isArray(messages) && messages.length === 0)) {
        const errorMessage = "Validation failed - no specific error details were returned";
        setErrorCounts(prev => ({
          ...prev,
          [errorMessage]: (prev[errorMessage] ?? 0) + 1,
        }));
        toast.error(errorMessage);
        return;
      }

      const errorMessage = Array.isArray(messages) ? messages.join(", ") : messages;

      // Update the count for this specific error message
      setErrorCounts(prev => ({
        ...prev,
        [errorMessage]: (prev[errorMessage] || 0) + 1,
      }));

      // Show toast with count if more than one song has this exact error
      const currentCount = (errorCounts[errorMessage] || 0) + 1;
      if (currentCount > 1) {
        toast.error(`${currentCount} songs have this error: ${errorMessage}`);
      }
      else {
        toast.error(errorMessage);
      }
    },
    onError: (message) => {
      console.error("Song creation error:", message);
      toast.error(message);
    },
  });

  // Validation progress state
  const [validationProgress, setValidationProgress] = useState<ValidationProgress>({
    isValidating: false,
    isUploading: false,
    isSuccess: false,
    currentSongIndex: 0,
    totalSongs: 0,
    uploadedSongs: 0,
    validationErrors: {},
    hasValidationErrors: false,
  });

  // Get validation status for all songs (skip variants/recordings - they attach to existing works)
  const allSongsValid = React.useMemo(() => {
    return Object.entries(songShares).every(([songId, songData]) => {
      // Check if this is a variant (intra-album match) or recording (existing account work match)
      const track = selectedTracks.find(t => t.id === songId);
      const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
      const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";

      // Variants and recordings don't need validation - they attach to existing works
      if (isVariant || isRecording)
        return true;

      if (songData == null || songData.writers.length === 0 || songData.performingArtists.length === 0)
        return false;
      const total = songData.writers.reduce((sum, writer) => sum + writer.writershare, 0);
      const hasRTC = songData.writers.some(writer => writer.righttocollect);
      const sharesAndRTCValid = Math.abs(total - 100) < 0.01 && hasRTC;

      // Add Tunecore-specific validation
      if (isTunecore) {
        const writersWithRTC = songData.writers.filter(writer => writer.righttocollect === true);

        for (const writer of writersWithRTC) {
          const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
          if (!fullWriter)
            continue;

          const hasDateOfBirth = Boolean(fullWriter.dateofbirth != null && fullWriter.dateofbirth.trim() !== "");
          const hasCAEIPI = Boolean(fullWriter.caeipinumber != null && fullWriter.caeipinumber.trim() !== "");

          if (!hasDateOfBirth || !hasCAEIPI) {
            return false;
          }
        }
      }

      return sharesAndRTCValid;
    });
  }, [songShares, selectedTracks, isTunecore, availableWriters]);

  // Validate all songs before submission (skip variants/recordings - they attach to existing works)
  const validateAllSongs = React.useCallback(async (): Promise<boolean> => {
    // Filter out variants and recordings - they don't need validation
    const songsToValidate = Object.entries(songShares).filter(([songId]) => {
      const track = selectedTracks.find(t => t.id === songId);
      const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
      const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";
      return !isVariant && !isRecording;
    });

    setValidationProgress({
      isValidating: true,
      isUploading: false,
      isSuccess: false,
      currentSongIndex: 0,
      totalSongs: songsToValidate.length,
      uploadedSongs: 0,
      validationErrors: {},
      hasValidationErrors: false,
    });

    const errors: Record<string, string[]> = {};
    let songIndex = 0;
    const songFormSchema = createSongFormSchema(isTunecore, availableWriters);

    for (const [songId, songData] of songsToValidate) {
      setValidationProgress(prev => ({
        ...prev,
        currentSongIndex: songIndex,
      }));

      // Simulate validation delay
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        songFormSchema.parse(songData);
      }
      catch (validationError) {
        if (validationError instanceof z.ZodError) {
          errors[songId] = validationError.errors.map(err => err.message);
        }
      }

      songIndex++;
    }

    const hasErrors = Object.keys(errors).length > 0;

    // Note: Detailed validation errors are shown in the dialog instead of toast notifications

    setValidationProgress(prev => ({
      ...prev,
      isValidating: false,
      validationErrors: errors,
      hasValidationErrors: hasErrors,
    }));

    return !hasErrors;
  }, [songShares, selectedTracks, isTunecore, availableWriters]);

  // Submit function for creating songs and recordings
  const submitAllSongs = React.useCallback(async () => {
    try {
      // Reset error counts for new submission
      setErrorCounts({});

      // Validate all songs first
      const isValid = await validateAllSongs();
      if (!isValid) {
        toast.error(t("album:fix-validation-errors-before-submit") ?? "Please go back and fix validation errors before submitting");
        return;
      }

      if (onSubmit) {
        await onSubmit(songShares);
      }

      const songDataArray = Object.values(songShares);
      const apiErrors: Record<string, string[]> = {}; // Track API validation errors

      // Group tracks: originals with their variants, standalone tracks, and account-level recordings
      const processedTrackIds = new Set<string>();
      const tracksToProcess: Array<{ songData: SongShareData; track: SelectedTrack; variants: Array<{ songData: SongShareData; track: SelectedTrack }> }> = [];
      const accountLevelRecordings: Array<{ songData: SongShareData; track: SelectedTrack }> = [];

      // First pass: Identify originals, variants, and account-level recordings
      for (const songData of songDataArray) {
        const track = selectedTracks.find(t => t.id === songData.songId);
        if (!track || processedTrackIds.has(track.id))
          continue;

        // Check if this is an account-level recording (existing work in account)
        const isAccountRecording = track.isSuspectedDuplicate === true
          && track.reviewAction !== "dismiss"
          && typeof track.suspectedWorkCode === "string"
          && track.suspectedWorkCode.length > 0;

        if (isAccountRecording) {
          // This is a recording of an existing work - handle separately
          accountLevelRecordings.push({ songData, track });
          processedTrackIds.add(track.id);
          continue;
        }

        // Check if this is a variant (points to another track in the album)
        const isVariant = typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track.reviewAction !== "dismiss";

        if (isVariant) {
          // This is a variant - skip for now, will be attached to its original
          continue;
        }

        // This is either: 1) An original track that may have variants, 2) A standalone new work, 3) An account-level recording
        const variants: Array<{ songData: SongShareData; track: SelectedTrack }> = [];

        // Find any variants that point to this track
        for (const variantSongData of songDataArray) {
          const variantTrack = selectedTracks.find(t => t.id === variantSongData.songId);
          if (!variantTrack || processedTrackIds.has(variantTrack.id))
            continue;

          const isVariantOfThis = variantTrack.suspectedAlbumTrackId === track.id && variantTrack.reviewAction !== "dismiss";
          if (isVariantOfThis) {
            variants.push({ songData: variantSongData, track: variantTrack });
            processedTrackIds.add(variantTrack.id);
          }
        }

        tracksToProcess.push({ songData, track, variants });
        processedTrackIds.add(track.id);
      }

      // Calculate total recordings to upload (works + all their recordings + account-level recordings)
      const totalRecordings = tracksToProcess.reduce((sum, { variants }) => sum + 1 + variants.length, 0) + accountLevelRecordings.length;

      // Start uploading phase with correct total
      setValidationProgress(prev => ({
        ...prev,
        isUploading: true,
        uploadedSongs: 0,
        totalSongs: totalRecordings,
      }));

      let uploadedCount = 0;

      // Build bulk array of works to create
      const worksToCreate: Array<CreateSongData> = [];

      const spotifyIdsToUpdate: string[] = [];

      // First: Process account-level recordings (attach to existing works)
      for (const { songData, track } of accountLevelRecordings) {
        try {
          // Fetch the existing work to get its etag
          const workData = await apiCall({
            endpoint: "/rightsapp/get",
            path: `/api/v1/work/getmywork?accountcode=${selectedAccount}&workcode=${track.suspectedWorkCode}`,
          }) as { etag?: string; workcode?: string } | null;

          if (workData == null || typeof workData.etag !== "string" || workData.etag.trim() === "") {
            throw new Error(`Could not fetch etag for work ${track.suspectedWorkCode}`);
          }

          const recordingData = {
            artistaccountcode: selectedAccount,
            workcode: track.suspectedWorkCode, // Attach to existing work
            title: songData.songTitle,
            performingartist: songData.performingArtists[0] || "",
            etag: workData.etag, // Required for creating recordings on existing works
            ...(((track.external_ids?.isrc) != null) && typeof track.external_ids.isrc === "string" && track.external_ids.isrc.trim() !== "" ? { isrc: track.external_ids.isrc } : {}),
            ...(track.id && typeof track.id === "string" && track.id.trim() !== "" ? { spotifyidentifier: track.id } : {}),
            ...(track.popularity != null ? { spotifypopularity: track.popularity } : {}),
            ...(track.explicit != null ? { explicit: track.explicit } : {}),
            ...(track.track_number != null ? { tracknumber: track.track_number } : {}),
            ...(track.disc_number != null ? { discnumber: track.disc_number } : {}),
            ...((() => {
              const sanitizedLabel = sanitizeRecordLabel(albumData?.label);
              return sanitizedLabel !== "" ? { recordlabel: sanitizedLabel } : {};
            })()),
            ...((() => {
              const releaseDate = track.album?.release_date;
              if (releaseDate != null && typeof releaseDate === "string" && releaseDate.trim() !== "") {
                // Convert year-only format ("1995") to full date ("1995-01-01")
                const dateStr = releaseDate.trim();
                if (/^\d{4}$/.test(dateStr)) {
                  return { releasedate: `${dateStr}-01-01` };
                }
                return { releasedate: releaseDate };
              }
              return {};
            })()),
          };

          await new Promise<void>((resolve, reject) => {
            createRecordingMutation.mutate(recordingData, {
              onSuccess: () => {
                uploadedCount++;
                setValidationProgress(prev => ({
                  ...prev,
                  uploadedSongs: uploadedCount,
                }));
                resolve();
              },
              onError: (error) => {
                console.error(`Error creating recording for "${songData.songTitle}":`, error);
                const errorMessage = error instanceof Error ? error.message : "Unknown error";
                apiErrors[songData.songId] = [errorMessage];
                reject(error);
              },
            });
          });

          // Update Spotify suggestion status (only if this track exists as a suggestion)
          if (track.id && suggestedSpotifyIds.has(track.id)) {
            try {
              await new Promise<void>((resolve) => {
                updateSuggestionMutation.mutate(
                  { spotify_song_id: track.id, status: 1 },
                  {
                    onSuccess: () => resolve(),
                    onError: () => {
                      console.warn(`Failed to update suggestion status for track ${track.id}`);
                      resolve();
                    },
                  },
                );
              });
            }
            catch (suggestionError) {
              console.warn(`Error updating suggestion status:`, suggestionError);
            }
          }
        }
        catch (error) {
          console.error(`Error processing recording for "${songData.songTitle}":`, error);
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          apiErrors[songData.songId] = [errorMessage];
          // Stop processing if we encounter errors with account-level recordings
          break;
        }
      }

      // If there were errors with account-level recordings, stop the submission
      if (Object.keys(apiErrors).length > 0) {
        setValidationProgress(prev => ({
          ...prev,
          isUploading: false,
          isSuccess: false,
          hasValidationErrors: true,
          validationErrors: apiErrors,
        }));
        toast.error(t("album:recording-creation-failed") ?? "Recording creation failed. Please review the errors.");
        return; // Stop submission
      }

      // Second: Process each new work (with its variants if any)
      for (const { songData, track, variants } of tracksToProcess) {
        // Collect Spotify IDs to update suggestion status (only for tracks that exist as suggestions)
        if (track.id && suggestedSpotifyIds.has(track.id)) {
          spotifyIdsToUpdate.push(track.id);
        }
        for (const { track: variantTrack } of variants) {
          if (variantTrack.id && suggestedSpotifyIds.has(variantTrack.id)) {
            spotifyIdsToUpdate.push(variantTrack.id);
          }
        }

        // Build the main recording for this work
        const mainRecording = {
          artistaccountcode: selectedAccount,
          title: songData.songTitle,
          performingartist: songData.performingArtists[0] || "",
          // Handle null/undefined/empty values properly - only include if they have valid, non-empty values
          ...(((track.external_ids?.isrc) != null) && typeof track.external_ids.isrc === "string" && track.external_ids.isrc.trim() !== "" ? { isrc: track.external_ids.isrc } : {}),
          ...(track.id && typeof track.id === "string" && track.id.trim() !== "" ? { spotifyidentifier: track.id } : {}),
          ...(track.popularity != null ? { spotifypopularity: track.popularity } : {}),
          ...(track.explicit != null ? { explicit: track.explicit } : {}),
          ...(track.track_number != null ? { tracknumber: track.track_number } : {}),
          ...(track.disc_number != null ? { discnumber: track.disc_number } : {}),
          ...((() => {
            const sanitizedLabel = sanitizeRecordLabel(albumData?.label);
            return sanitizedLabel !== "" ? { recordlabel: sanitizedLabel } : {};
          })()),
          ...((() => {
            const releaseDate = track.album?.release_date;
            if (releaseDate != null && typeof releaseDate === "string" && releaseDate.trim() !== "") {
              // Convert year-only format ("1995") to full date ("1995-01-01")
              const dateStr = releaseDate.trim();
              if (/^\d{4}$/.test(dateStr)) {
                return { releasedate: `${dateStr}-01-01` };
              }
              return { releasedate: releaseDate };
            }
            return {};
          })()),
        };

        // Build variant recordings (intra-album matches)
        const variantRecordings = variants.map(({ songData: variantSongData, track: variantTrack }) => ({
          artistaccountcode: selectedAccount,
          title: variantSongData.songTitle,
          performingartist: variantSongData.performingArtists[0] || "",
          ...(((variantTrack.external_ids?.isrc) != null) && typeof variantTrack.external_ids.isrc === "string" && variantTrack.external_ids.isrc.trim() !== "" ? { isrc: variantTrack.external_ids.isrc } : {}),
          ...(variantTrack.id && typeof variantTrack.id === "string" && variantTrack.id.trim() !== "" ? { spotifyidentifier: variantTrack.id } : {}),
          ...(variantTrack.popularity != null ? { spotifypopularity: variantTrack.popularity } : {}),
          ...(variantTrack.explicit != null ? { explicit: variantTrack.explicit } : {}),
          ...(variantTrack.track_number != null ? { tracknumber: variantTrack.track_number } : {}),
          ...(variantTrack.disc_number != null ? { discnumber: variantTrack.disc_number } : {}),
          ...((() => {
            const sanitizedLabel = sanitizeRecordLabel(albumData?.label);
            return sanitizedLabel !== "" ? { recordlabel: sanitizedLabel } : {};
          })()),
          ...((() => {
            const releaseDate = variantTrack.album?.release_date;
            if (releaseDate != null && typeof releaseDate === "string" && releaseDate.trim() !== "") {
              // Convert year-only format ("1995") to full date ("1995-01-01")
              const dateStr = releaseDate.trim();
              if (/^\d{4}$/.test(dateStr)) {
                return { releasedate: `${dateStr}-01-01` };
              }
              return { releasedate: releaseDate };
            }
            return {};
          })()),
        }));

        worksToCreate.push({
          artistaccountcode: selectedAccount,
          title: songData.songTitle,
          durationmins: songData.durationMins,
          durationsecs: songData.durationSecs,
          isremix: track.isRemix ?? false,
          containssamples: track.containsSamples ?? false,
          ispublicdomain: false,
          alternativetitles: songData.alternativeTitles.map(title => ({ title })),
          performingartistnames: songData.performingArtists.map(name => ({ name })),
          writersplits: songData.writers.map(writer => ({
            writercode: writer.writercode,
            writershare: writer.writershare,
            writerdesignationcode: writer.writerdesignationcode, // Integer, not string
            righttocollect: writer.righttocollect,
          })),
          recordings: [mainRecording, ...variantRecordings], // Include main + all variants
        });
      }

      if (worksToCreate.length > 0) {
        try {
          await createSongs(worksToCreate, source === "playlist" ? "AddPlaylist" : "AddAlbum");

          const newUploadedCount = tracksToProcess.reduce(
            (sum, { variants }) => sum + 1 + variants.length,
            0,
          );
          uploadedCount += newUploadedCount;

          setValidationProgress(prev => ({
            ...prev,
            uploadedSongs: uploadedCount,
          }));

          // Update suggestion statuses so processed songs are removed from the SuggestionsTable
          if (spotifyIdsToUpdate.length > 0) {
            const suggestionResults = await Promise.allSettled(
              spotifyIdsToUpdate.map(async spotifyId =>
                updateSuggestionMutation.mutateAsync({ spotify_song_id: spotifyId, status: 1 }),
              ),
            );

            const failedUpdates = suggestionResults
              .map((result, index) => ({ result, spotifyId: spotifyIdsToUpdate[index] }))
              .filter(({ result }) => result.status === "rejected");

            if (failedUpdates.length > 0) {
              for (const { spotifyId } of failedUpdates) {
                console.warn(`Failed to update suggestion status for track ${spotifyId}`);
              }
            }
          }
        }
        catch (error) {
          // Capture API validation errors for all songs in the batch
          console.error("Error creating songs batch:", error);
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          for (const { songData } of tracksToProcess) {
            apiErrors[songData.songId] = [errorMessage];
          }
        }
      }

      // Check if there were any API errors
      if (Object.keys(apiErrors).length > 0) {
        // Show validation errors in the modal
        setValidationProgress(prev => ({
          ...prev,
          isUploading: false,
          isSuccess: false,
          hasValidationErrors: true,
          validationErrors: apiErrors,
        }));
        toast.error(t("album:some-songs-failed-to-create") ?? `${Object.keys(apiErrors).length} song(s) failed to create. Please review the errors.`);
        return; // Don't show success
      }

      // Show success phase
      setValidationProgress(prev => ({
        ...prev,
        isUploading: false,
        isSuccess: true,
      }));

      toast.success(t("album:songs-created-successfully", { count: uploadedCount }));
    }
    catch (error) {
      console.error("Error creating songs:", error);
      setValidationProgress(prev => ({
        ...prev,
        isUploading: false,
        isSuccess: false,
      }));
      toast.error(t("album:error-creating-songs"));
    }
  }, [songShares, selectedTracks, selectedAccount, createSongs, onSubmit, validateAllSongs, albumData?.label, t, updateSuggestionMutation, createRecordingMutation, suggestedSpotifyIds, source]);

  // Expose submit function for parent component
  React.useEffect(() => {
    if (onValidationComplete) {
      onValidationComplete(allSongsValid, submitAllSongs);
    }
  }, [allSongsValid, submitAllSongs, onValidationComplete]);

  const songDataArray = Object.values(songShares);

  if (songDataArray.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className={`text-sm ${isTunecore ? "text-gray-300" : "text-gray-500"}`}>
          {t("album:no-songs-found")}
        </Text>
      </div>
    );
  }

  return (
    <>

      {/* Overall Status */}
      <div className={`rounded-lg p-4 text-center ${isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-light-grey"}`}>
        <Text as="p" className={`font-semibold ${allSongsValid ? "text-green-600" : "text-red-600"}`}>
          {allSongsValid
            ? (() => {
                // Calculate breakdown of songs
                const newSongs = songDataArray.filter((song) => {
                  const track = selectedTracks.find(t => t.id === song.songId);
                  const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
                  const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";
                  return !isVariant && !isRecording;
                }).length;

                const recordings = songDataArray.filter((song) => {
                  const track = selectedTracks.find(t => t.id === song.songId);
                  const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
                  const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";
                  return isVariant || isRecording;
                }).length;

                if (recordings > 0) {
                  return t("album:all-songs-ready-with-breakdown", { total: songDataArray.length, newSongs, recordings })
                    ?? `All ${songDataArray.length} songs ready! (${newSongs} new registration${newSongs !== 1 ? "s" : ""}, ${recordings} recording${recordings !== 1 ? "s" : ""})`;
                }

                return t("album:all-songs-ready", { count: songDataArray.length }) ?? `All ${songDataArray.length} songs are ready for submission!`;
              })()
            : (() => {
                const invalidSongsCount = songDataArray.filter((song) => {
                  // Check if this is a variant or recording - skip these from error count
                  const track = selectedTracks.find(t => t.id === song.songId);
                  const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
                  const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";

                  // Variants and recordings are always "valid" - they attach to existing works
                  if (isVariant || isRecording)
                    return false;

                  const writersValid = song.writers.length > 0
                    && Math.abs(song.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
                    && song.writers.some(w => w.righttocollect);
                  const performingArtistsValid = song.performingArtists.length > 0;
                  const isValid = writersValid && performingArtistsValid;
                  return !isValid;
                }).length;
                return t("album:songs-need-attention", { count: invalidSongsCount }) ?? `⚠️ ${invalidSongsCount} song(s) need attention before submission`;
              })()}
        </Text>
      </div>

      {/* Songs Summary List */}
      <ScrollArea className={`h-[800px] ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg"}`} id="albumReviewStep">

        {/* Validation warnings summary */}
        {(() => {
          const songsWithTitleWarnings = songDataArray.filter(song => getTitleValidationInfo(song.songTitle).hasInvalidChars);
          const songsWithAltTitleWarnings = songDataArray.filter(song =>
            song.alternativeTitles.some(title => title.length > 0 && !validateAlternativeTitle(title).isValid),
          );
          const songsWithArtistWarnings = songDataArray.filter(song =>
            song.performingArtists.some(artist => artist.length > 0 && !validatePerformingArtist(artist).isValid),
          );
          const totalWarnings = songsWithTitleWarnings.length + songsWithAltTitleWarnings.length + songsWithArtistWarnings.length;

          if (totalWarnings > 0) {
            const warningMessages = [];

            if (songsWithTitleWarnings.length > 0) {
              warningMessages.push(
                t("album:songs-with-invalid-title-characters", { count: songsWithTitleWarnings.length })
                ?? `${songsWithTitleWarnings.length} song${songsWithTitleWarnings.length > 1 ? "s" : ""} have invalid title characters.`,
              );
            }

            if (songsWithAltTitleWarnings.length > 0) {
              warningMessages.push(
                t("album:songs-with-invalid-alternative-title-characters", { count: songsWithAltTitleWarnings.length })
                ?? `${songsWithAltTitleWarnings.length} song${songsWithAltTitleWarnings.length > 1 ? "s" : ""} have invalid alternative title characters.`,
              );
            }

            if (songsWithArtistWarnings.length > 0) {
              warningMessages.push(
                t("album:songs-with-invalid-artist-characters", { count: songsWithArtistWarnings.length })
                ?? `${songsWithArtistWarnings.length} song${songsWithArtistWarnings.length > 1 ? "s" : ""} have invalid performing artist characters.`,
              );
            }

            warningMessages.push(
              t("album:auto-corrected-submission-warning") ?? "These will be auto-corrected during submission, but you may want to review them.",
            );

            return (
              <ErrorContainer
                variant="warning"
                title={t("album:character-validation-warnings-detected") ?? "Character Validation Warnings Detected"}
                validationIssues={warningMessages}
                className="mt-3 mb-4"
              />
            );
          }
          return null;
        })()}

        {/* {!allSongsValid && (
          <Text as="p" className={`my-2 ${isTunecore ? "text-red-400" : "text-red-600"}`}>
            {t("album:fix-validation-issues") ?? "Please go back to the previous step to fix the validation issues."}
          </Text>
        )} */}

        <Accordion type="multiple" className="space-y-2">
          {songDataArray.map((songData, index) => {
            // Check if this is a variant/recording (not expandable)
            const track = selectedTracks.find(t => t.id === songData.songId);
            const isVariant = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track?.reviewAction !== "dismiss";
            const isRecording = track?.isSuspectedDuplicate === true && track?.reviewAction !== "dismiss";
            const isVariantOrRecording = isVariant || isRecording;

            // Check all validation criteria to determine if song is valid (skip for variants and recordings)
            const writersValid = songData.writers.length > 0
              && Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
              && songData.writers.some(w => w.righttocollect);
            const performingArtistsValid = songData.performingArtists.length > 0;
            const hasBusinessValidationErrors = validationProgress.validationErrors[songData.songId]?.length > 0;
            const isValid = isVariantOrRecording || (writersValid && performingArtistsValid && !hasBusinessValidationErrors);

            // Check for title validation issues (skip for variants and recordings)
            const titleValidation = !isVariantOrRecording ? getTitleValidationInfo(songData.songTitle) : { hasInvalidChars: false, suggestedTitle: "" };
            const invalidAlternativeTitles = !isVariantOrRecording ? songData.alternativeTitles
              .map((title, idx) => ({ title, index: idx, ...validateAlternativeTitle(title) }))
              .filter(item => !item.isValid && item.title.length > 0) : [];
            const invalidPerformingArtists = !isVariantOrRecording ? songData.performingArtists
              .map((artist, idx) => ({ artist, index: idx, ...validatePerformingArtist(artist) }))
              .filter(item => !item.isValid && item.artist.length > 0) : [];

            const hasValidationWarnings = !isVariantOrRecording && (titleValidation.hasInvalidChars || invalidAlternativeTitles.length > 0 || invalidPerformingArtists.length > 0);

            return (
              <AccordionItem key={songData.songId} value={isVariantOrRecording ? "" : songData.songId} className={`rounded-lg shadow-sm ${isVariantOrRecording ? "opacity-75" : ""} ${isTunecore ? "bg-tunecore-grey-element border border-tunecore-mid-grey" : "bg-white border"}`}>
                <AccordionTrigger disabled={isVariantOrRecording} className={`px-6 rounded-lg py-4 hover:no-underline transition-colors ${isVariantOrRecording ? "cursor-default opacity-100" : ""} ${isTunecore && !isVariantOrRecording ? "hover:bg-tunecore-light-grey" : !isTunecore && !isVariantOrRecording ? "hover:bg-sentric-mid-grey" : ""}`}>
                  <div className="flex items-center w-full gap-4">
                    <AccordionItemNumber
                      number={index + 1}
                      hasError={!isValid || hasValidationWarnings}
                    />
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <Text as="subtitle">
                          {songData.songTitle}
                        </Text>

                        {/* Recording Badge - Show if this is being added as a recording to existing work or intra-album track */}
                        {(() => {
                          const track = selectedTracks.find(t => t.id === songData.songId);
                          const isRecording = track?.isSuspectedDuplicate && track?.reviewAction !== "dismiss";

                          // Check for intra-album match first (takes precedence)
                          const isIntraAlbumMatch = typeof track?.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0;
                          const targetTitle = isIntraAlbumMatch
                            ? track?.suspectedAlbumTrackTitle
                            : track?.suspectedWorkTitle;

                          if (isRecording && typeof targetTitle === "string" && targetTitle.length > 0) {
                            const badgeLabel = isIntraAlbumMatch
                              ? (t("album:variant-of") ?? "Variant of")
                              : (t("album:recording-for") ?? "Recording for");

                            return (
                              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${
                                isTunecore
                                  ? "bg-tunecore-green/20 border-tunecore-green/50 text-tunecore-green"
                                  : "bg-green-500"
                              }`}
                              >
                                <IconTick className={`w-4 h-4 flex-shrink-0 ${isTunecore ? "stroke-tunecore-green" : "stroke-black"}`} />
                                <Text as="p">
                                  {badgeLabel}
                                  :
                                  {" "}
                                  <span className="font-semibold">{targetTitle}</span>

                                </Text>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {!isVariantOrRecording && (
                          <Button
                            className="self-center p-1 mb-1 group"
                            variant="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSong(songData.songId);
                            }}
                            icon={<IconCreate className={`w-5 h-5 self-center ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black group-hover:stroke-sentric-orange"}`} />}
                          />
                        )}
                        {/* {isValid ? (
                          <IconTick className="w-5 h-5 text-green-500" />
                        ) : (
                          <div className="flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
                            <span className="text-xs font-bold text-white">!</span>
                          </div>
                        )}
                        {hasValidationWarnings && (
                          <div className="flex items-center justify-center w-5 h-5 bg-yellow-500 rounded-full">
                            <span className="text-xs font-bold text-white">⚠</span>
                          </div>
                        )} */}
                      </div>

                      {/* Title validation warning */}
                      {!isVariantOrRecording && titleValidation.hasInvalidChars && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-yellow-600/20 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                          <Text as="p" className={isTunecore ? "text-yellow-400" : "text-yellow-800"}>
                            <strong>
                              {t("album:title-warning") ?? "Title Warning"}
                              :
                            </strong>
                            {" "}
                            {t("album:contains-invalid-characters-suggested", { suggestedTitle: titleValidation.suggestedTitle })
                              ?? `Contains invalid characters. Suggested: "${titleValidation.suggestedTitle}"`}
                          </Text>
                        </div>
                      )}

                      {/* Alternative titles validation warnings */}
                      {!isVariantOrRecording && invalidAlternativeTitles.length > 0 && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-yellow-600/20 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                          <Text as="p" className={isTunecore ? "text-yellow-400" : "text-yellow-800"}>
                            <strong>
                              {t("album:alternative-title-warnings") ?? "Alternative Title Warnings"}
                              :
                            </strong>
                            {" "}
                            {invalidAlternativeTitles
                              .map(item => (
                                t("album:contains-invalid-characters-suggested", { suggestedTitle: item.suggestedTitle })
                                ?? `Contains invalid characters. Suggested: "${item.suggestedTitle}"`
                              ))
                              .join(" ")}
                          </Text>
                        </div>
                      )}

                      {/* Performing artist validation warnings */}
                      {!isVariantOrRecording && invalidPerformingArtists.length > 0 && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-yellow-600/20 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                          <Text as="p" className={isTunecore ? "text-yellow-400" : "text-yellow-800"}>
                            <strong>
                              {t("album:performing-artist-warnings") ?? "Performing Artist Warnings"}
                              :
                            </strong>
                            {" "}
                            {invalidPerformingArtists
                              .map(item => (
                                t("album:contains-invalid-characters-suggested", { suggestedName: item.suggestedName })
                                ?? `Contains invalid characters. Suggested: "${item.suggestedName}"`
                              ))
                              .join(" ")}
                          </Text>
                        </div>
                      )}

                      {/* Business validation errors */}
                      {!isVariantOrRecording && validationProgress.validationErrors[songData.songId]?.length > 0 && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-red-600/20 border-red-600" : "bg-red-50 border-red-200"}`}>
                          <Text as="p" className={isTunecore ? "text-red-400" : "text-red-800"}>
                            <strong>
                              {t("album:business-validation-errors") ?? "Validation Errors"}
                              :
                            </strong>
                            <ul className="mt-1 list-disc list-inside">
                              {validationProgress.validationErrors[songData.songId].map(error => (
                                <li key={error}>{error}</li>
                              ))}
                            </ul>
                          </Text>
                        </div>
                      )}

                      <div className="flex flex-col items-start gap-1 max-md:mt-2 md:gap-6 md:items-center md:flex-row ">
                        <span>
                          {t("album:duration") ?? "Duration"}
                          {": "}
                          {songData.durationMins}
                          :
                          {songData.durationSecs.toString().padStart(2, "0")}
                        </span>
                        <span>
                          {t("album:performing-artists") ?? "Performing Artists"}
                          {": "}
                          {" "}
                          {songData.performingArtists.join(", ")}
                        </span>
                      </div>
                    </div>

                  </div>
                </AccordionTrigger>

                {!isVariantOrRecording && (
                  <AccordionContent className="px-6 pt-2 pb-4">
                    <div className="space-y-2">
                      {/* Alternative Titles */}
                      {songData.alternativeTitles.length > 0 && (
                        <div>
                          <Text as="p" className={`font-medium mb-1 ${isTunecore ? "text-gray-300" : ""}`}>
                            {t("album:alternative-titles") ?? "Alternative Titles"}
                            :
                          </Text>
                          <Text as="p" className={isTunecore ? "text-gray-400" : ""}>
                            {songData.alternativeTitles.join(", ")}
                          </Text>
                        </div>
                      )}

                      {/* Writers Section */}
                      <div>
                        <Text as="p" className={`font-medium mb-2 ${isTunecore ? "text-gray-300" : ""}`}>
                          {/* Writers & Shares: */}
                          {t("album:writers-and-shares") ?? "Writers & Shares"}
                        </Text>
                        <div className="space-y-2">
                          {songData.writers.map((writer, _writerIndex) => {
                            const designation = DESIGNATION_OPTIONS.find(opt => opt.value === writer.writerdesignationcode)?.label ?? t("album:unknown") ?? "Unknown";
                            return (
                              <div key={writer.writercode} className={`flex items-center justify-between p-3 rounded ${isTunecore ? "bg-tunecore-light-grey" : "bg-sentric-light-grey"}`}>
                                <div className="flex-1">
                                  <Text as="p" className={`font-medium ${isTunecore ? "text-white" : ""}`}>
                                    {writer.writername}
                                  </Text>
                                  <Text as="small">
                                    {writer.writercode}
                                    {" "}
                                    /
                                    {" "}
                                    {designation}
                                  </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                  {writer.righttocollect && (
                                    <Text as="small" className={`text-xs px-2 py-1 rounded ${isTunecore ? "bg-green-600 text-green-200" : "bg-green-100 text-green-800"}`}>
                                      {/* Right To Collect */}
                                      {t("album:right-to-collect") ?? "Right To Collect"}
                                    </Text>
                                  )}
                                  <Text as="p" className={`font-medium ${isTunecore ? "text-white" : ""}`}>
                                    {writer.writershare.toFixed(2)}
                                    %
                                  </Text>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-2 text-right">
                          <Text
                            as="p"
                            className={` font-medium ${
                              Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            Total:
                            {" "}
                            {songData.writers.reduce((sum, w) => sum + w.writershare, 0).toFixed(2)}
                            %
                          </Text>
                        </div>
                      </div>

                      {/* Validation Status */}
                      {!isValid && (
                        <ErrorContainer
                          variant="destructive"
                          title={t("album:validation-issues-detected") ?? "This song has validation issues:"}
                          validationIssues={[
                            ...(songData.writers.length === 0 ? [t("album:no-writers-assigned") ?? "No writers assigned"] : []),
                            ...(Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) > 0.01 ? [t("album:writer-shares-total-100") ?? "Writer shares must total 100%"] : []),
                            ...(!songData.writers.some(w => w.righttocollect) ? [t("album:no-writer-right-to-collect") ?? "At least one writer must have 'Right To Collect' enabled"] : []),
                            ...(songData.performingArtists.length === 0 ? [t("album:no-performing-artists-assigned") ?? "At least one performing artist is required"] : []),
                          ]}
                        />
                      )}
                    </div>
                  </AccordionContent>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      </ScrollArea>

      {/* Validation/Upload/Success Modal */}
      <Dialog
        open={validationProgress.isValidating || validationProgress.isUploading || validationProgress.isSuccess || validationProgress.hasValidationErrors}
        onOpenChange={(open) => {
          // Prevent closing the modal if submission was successful
          if (!open && validationProgress.isSuccess) {
            return;
          }
          if (!open) {
            setValidationProgress(prev => ({
              ...prev,
              isValidating: false,
              hasValidationErrors: false,
              isSuccess: false,
            }));
          }
        }}
      >
        <DialogContent
          className={`max-w-2xl max-h-[80vh] ${isTunecore ? "bg-[#282828] text-white border-gray-600" : ""}`}
          onEscapeKeyDown={(e) => {
            // Prevent ESC key from closing modal when successful
            if (validationProgress.isSuccess) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Prevent clicking outside from closing modal when successful
            if (validationProgress.isSuccess) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {validationProgress.isValidating && (t("album:validating-songs") ?? "Validating Songs")}
              {validationProgress.isUploading && (t("album:creating-songs") ?? "Creating Songs")}
              {validationProgress.isSuccess && (t("album:songs-created-successfully-final") ?? "Songs Created Successfully")}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-4">
            {/* Validation Phase */}
            {(validationProgress.isValidating || validationProgress.hasValidationErrors) && (
              <>
                {validationProgress.isValidating && (
                  <>
                    <div className="flex items-center gap-3">
                      <Text as="p">
                        {t("album:validating-song") ?? "Validating song"}
                        {" "}
                        {validationProgress.currentSongIndex + 1}
                        {" "}
                        {t("album:of") ?? "of"}
                        {" "}
                        {validationProgress.totalSongs}
                      </Text>
                    </div>

                    <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                      <div
                        className="h-2 transition-all duration-300 rounded-full"
                        style={{
                          backgroundColor: highlightColour2,
                          width: `${validationProgress.totalSongs > 0 ? (validationProgress.currentSongIndex / validationProgress.totalSongs) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </>
                )}

                {/* { IN LOCIZE
                    "validation-errors-detected_one": "⚠️ {{count}} song has validation errors",
                    "validation-errors-detected_other": "⚠️ {{count}} songs have validation errors",
                    "close-dialog-fix-errors": "Please close this dialog and go back to fix the errors before submitting.",
                    "close-go-back": "Close & Go Back"
                  } */}
                {validationProgress.hasValidationErrors && (
                  <>
                    {(() => {
                      // Transform validation errors into formErrors format for ErrorContainer
                      const formErrors: Record<string, { message: string }> = {};

                      Object.entries(validationProgress.validationErrors).forEach(([songId, songErrors]) => {
                        const songData = songShares[songId];
                        const songTitle = songData?.songTitle ?? "Unknown Song";
                        formErrors[`"${songTitle}"`] = {
                          message: songErrors.join(", "),
                        };
                      });

                      return (
                        <ErrorContainer
                          variant="destructive"
                          title={t("album:dialog-validation-errors-detected", {
                            count: Object.keys(validationProgress.validationErrors).length,
                          }) ?? `⚠️ ${Object.keys(validationProgress.validationErrors).length} song(s) have validation errors`}
                          message={t("album:close-dialog-fix-errors") ?? "Please close this dialog and go back to fix the errors before submitting."}
                          formErrors={formErrors}
                        />
                      );
                    })()}

                    <div className="text-center">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => setValidationProgress(prev => ({ ...prev, hasValidationErrors: false }))}
                        label={t("album:close-go-back") ?? "Close & Go Back"}
                        className="bg-red-600 hover:bg-red-700"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Upload Phase {
              "creating-song-progress": "Creating song {{current}} of {{total}}",
              "creating-songs-in-account": "Creating songs in your account..."
            } */}
            {validationProgress.isUploading && (
              <>
                <div className="flex items-center gap-3">
                  <Text as="p">
                    {t("album:creating-song-progress", {
                      current: validationProgress.uploadedSongs + 1,
                      total: validationProgress.totalSongs,
                    }) ?? `Creating song ${validationProgress.uploadedSongs + 1} of ${validationProgress.totalSongs}`}
                  </Text>
                </div>

                <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div
                    className="h-2 transition-all duration-300 bg-green-600 rounded-full"
                    style={{
                      width: `${validationProgress.totalSongs > 0 ? (validationProgress.uploadedSongs / validationProgress.totalSongs) * 100 : 0}%`,
                    }}
                  />
                </div>

                <div className={`rounded-lg p-3 border ${isTunecore ? "bg-blue-900/20 border-blue-600" : "bg-blue-50 border-blue-200"}`}>
                  <Text as="p" className={`${isTunecore ? "text-blue-400" : "text-blue-800"}`}>
                    {t("album:creating-songs-in-account") ?? "Creating songs in your account..."}
                  </Text>
                </div>
              </>
            )}

            {/* Success Phase  {
                "all-songs-created-successfully": "All {{count}} songs created successfully!",
                "songs-added-to-catalog": "Your songs have been successfully added to your catalog! You can now view and manage them.",
                "back-to-songs": "Back to Songs"
              } */}
            {validationProgress.isSuccess && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-5 h-5 bg-green-600 rounded-full">
                    <IconTick className="w-3 h-3 text-white" />
                  </div>
                  <Text as="p" className={`text-green-800 font-medium ${isTunecore ? "text-green-400" : ""}`}>
                    {t("album:all-songs-created-successfully", { count: validationProgress.totalSongs })
                      ?? `All ${validationProgress.totalSongs} songs created successfully!`}
                  </Text>
                </div>

                <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div className="w-full h-2 bg-green-600 rounded-full"></div>
                </div>

                <div className={`rounded-lg p-3 border ${isTunecore ? "bg-green-900/20 border-green-600" : "bg-green-50 border-green-200"}`}>
                  <Text as="p" className={`mb-3 ${isTunecore ? "text-green-400" : "text-green-800"}`}>
                    {t("album:songs-added-to-catalog") ?? "Your songs have been successfully added to your catalog! You can now view and manage them."}
                  </Text>
                  <Button
                    onClick={() => {
                      window.location.href = "/songs";
                    }}
                    variant="primary"
                    label={t("album:back-to-songs") ?? "Back to Songs"}
                  />
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>

  );
}
